import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Shield,
  Users,
  FileText,
  Store,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  AlertTriangle,
  Loader2,
  Eye,
  Trash2,
  Ban,
  MessageSquareWarning,
  Flag
} from 'lucide-react';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportAction, setReportAction] = useState('dismiss');
  const [actionLoading, setActionLoading] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error(language === 'cr' ? 'Aksé intèrdi' : 'Accès interdit');
      navigate('/feed');
    }
  }, [user, navigate, language]);

  // Fetch admin data
  const fetchAdminData = useCallback(async () => {
    if (!user?.is_admin) return;
    
    setLoading(true);
    try {
      const [statsRes, verificationsRes, reportsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/commerce/pending'),
        api.get('/admin/reports/pending')
      ]);
      
      setStats(statsRes.data);
      setPendingVerifications(verificationsRes.data);
      setRecentReports(reportsRes.data || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      // Stats might not exist yet, use defaults
      setStats({
        total_users: 0,
        total_posts: 0,
        total_commerces: 0,
        verified_commerces: 0,
        pending_verifications: 0,
        pending_reports: 0
      });
    } finally {
      setLoading(false);
    }
  }, [api, user?.is_admin]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  const handleVerificationAction = async (action) => {
    if (!selectedRequest) return;
    
    setActionLoading(true);
    try {
      await api.post(`/admin/commerce/verify/${selectedRequest.request_id}?action=${action}`, null, {
        params: { notes: reviewNotes || undefined }
      });
      
      toast.success(
        action === 'approve' 
          ? (language === 'cr' ? 'Komèrs vérifié !' : 'Commerce vérifié !')
          : (language === 'cr' ? 'Domand refuzé' : 'Demande refusée')
      );
      
      setSelectedRequest(null);
      setReviewNotes('');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReportAction = async () => {
    if (!selectedReport) return;
    
    setActionLoading(true);
    try {
      await api.post(`/admin/reports/${selectedReport.report_id}/action`, null, {
        params: { 
          action: reportAction,
          notes: reviewNotes || undefined 
        }
      });
      
      const messages = {
        dismiss: language === 'cr' ? 'Sinyalman ignoré' : 'Signalement ignoré',
        delete_post: language === 'cr' ? 'Post siprimé' : 'Post supprimé',
        warn_user: language === 'cr' ? 'Avèrtisman anvoyé' : 'Avertissement envoyé',
        ban_user: language === 'cr' ? 'Itilizatèr bani' : 'Utilisateur banni'
      };
      
      toast.success(messages[reportAction] || 'Action effectuée');
      setSelectedReport(null);
      setReviewNotes('');
      setReportAction('dismiss');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const getReasonLabel = (reason) => {
    const labels = {
      spam: { fr: 'Spam', cr: 'Spam', color: 'bg-yellow-500/10 text-yellow-600' },
      inappropriate: { fr: 'Inapproprié', cr: 'Inapropryé', color: 'bg-orange-500/10 text-orange-600' },
      harassment: { fr: 'Harcèlement', cr: 'Arsèlman', color: 'bg-red-500/10 text-red-600' },
      misinformation: { fr: 'Désinformation', cr: 'Dezinformasyon', color: 'bg-purple-500/10 text-purple-600' },
      other: { fr: 'Autre', cr: 'Lòt', color: 'bg-gray-500/10 text-gray-600' }
    };
    return labels[reason] || labels.other;
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen tropic-bg flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">
              {language === 'cr' ? 'Aksé Intèrdi' : 'Accès Interdit'}
            </h2>
            <p className="text-muted-foreground mb-4">
              {language === 'cr' 
                ? 'Ou na pa lo drwa aksédé sa paz la.'
                : 'Vous n\'avez pas les droits pour accéder à cette page.'}
            </p>
            <Button onClick={() => navigate('/feed')}>
              {language === 'cr' ? 'Rétourné lo fil' : 'Retour au fil'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen tropic-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b">
        <div className="container mx-auto max-w-6xl px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/feed')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-xl font-bold">
                  {language === 'cr' ? 'Administrasyon' : 'Administration'}
                </h1>
                <p className="text-xs text-muted-foreground">
                  {language === 'cr' ? 'Jèr laplikasyon' : 'Gérez l\'application'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl px-4 py-6 space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Users className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'cr' ? 'Itilizatèr' : 'Utilisateurs'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <FileText className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_posts || 0}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'cr' ? 'Piblikasyon' : 'Publications'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <Store className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_commerces || 0}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'cr' ? 'Komèrs' : 'Commerces'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.pending_verifications || pendingVerifications.length}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'cr' ? 'An atant' : 'En attente'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="verifications" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="verifications" className="flex items-center gap-2">
              <Store className="h-4 w-4" />
              {language === 'cr' ? 'Vérifikasyon' : 'Vérifications'}
              {pendingVerifications.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs flex items-center justify-center">
                  {pendingVerifications.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {language === 'cr' ? 'Sinyalman' : 'Signalements'}
            </TabsTrigger>
          </TabsList>

          {/* Verifications Tab */}
          <TabsContent value="verifications" className="mt-4 space-y-4">
            {pendingVerifications.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-medium">
                    {language === 'cr' ? 'Tout lé an ord !' : 'Tout est en ordre !'}
                  </p>
                  <p className="text-muted-foreground">
                    {language === 'cr' 
                      ? 'Nana pwin domand vérifikasyon an atant.'
                      : 'Aucune demande de vérification en attente.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              pendingVerifications.map((request) => (
                <Card key={request.request_id} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      <div className="flex-1 p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-bold text-lg">{request.business_name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {request.user?.name} • {request.user?.email}
                            </p>
                          </div>
                          <Badge variant="outline" className="bg-orange-500/10 text-orange-600">
                            <Clock className="h-3 w-3 mr-1" />
                            {language === 'cr' ? 'An atant' : 'En attente'}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">
                              {language === 'cr' ? 'Tip:' : 'Type:'}
                            </span>
                            <span className="ml-2 font-medium">{request.business_type}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">
                              {language === 'cr' ? 'Kominn:' : 'Commune:'}
                            </span>
                            <span className="ml-2 font-medium">{request.user?.commune}</span>
                          </div>
                          {request.business_registration && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">SIRET:</span>
                              <span className="ml-2 font-mono">{request.business_registration}</span>
                            </div>
                          )}
                        </div>

                        {request.additional_info && (
                          <div className="text-sm bg-muted/50 p-2 rounded">
                            <p className="text-muted-foreground text-xs mb-1">
                              {language === 'cr' ? 'Info siplémentèr:' : 'Info supplémentaire:'}
                            </p>
                            <p>{request.additional_info}</p>
                          </div>
                        )}

                        <p className="text-xs text-muted-foreground">
                          {language === 'cr' ? 'Soumèt:' : 'Soumis:'} {new Date(request.submitted_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>

                      <div className="flex md:flex-col gap-2 p-4 bg-muted/30 md:w-40 justify-center">
                        <Button 
                          className="flex-1 md:flex-none bg-green-600 hover:bg-green-700"
                          onClick={() => setSelectedRequest({ ...request, action: 'approve' })}
                          data-testid={`approve-${request.request_id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {language === 'cr' ? 'Aprouvé' : 'Approuver'}
                        </Button>
                        <Button 
                          variant="destructive"
                          className="flex-1 md:flex-none"
                          onClick={() => setSelectedRequest({ ...request, action: 'reject' })}
                          data-testid={`reject-${request.request_id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          {language === 'cr' ? 'Refuzé' : 'Refuser'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-4 space-y-4">
            {recentReports.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-medium">
                    {language === 'cr' ? 'Nana pwin sinyalman' : 'Aucun signalement'}
                  </p>
                  <p className="text-muted-foreground">
                    {language === 'cr' 
                      ? 'La kominité i komport byincomifaut !'
                      : 'La communauté se comporte bien !'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              recentReports.map((report) => {
                const reasonInfo = getReasonLabel(report.reason);
                return (
                  <Card key={report.report_id} className="overflow-hidden">
                    <CardContent className="p-0">
                      <div className="flex flex-col md:flex-row">
                        <div className="flex-1 p-4 space-y-3">
                          {/* Report Header */}
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <Flag className="h-4 w-4 text-destructive" />
                              <Badge className={`${reasonInfo.color} rounded-full`}>
                                {language === 'cr' ? reasonInfo.cr : reasonInfo.fr}
                              </Badge>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(report.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>

                          {/* Reported Content */}
                          {report.post && (
                            <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                              <p className="font-medium text-sm">{report.post.title}</p>
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {report.post.content}
                              </p>
                              <p className="text-xs text-primary">
                                {language === 'cr' ? 'Par:' : 'Par:'} {report.post.user_name}
                              </p>
                            </div>
                          )}

                          {report.reported_user && !report.post && (
                            <div className="bg-muted/50 rounded-lg p-3">
                              <p className="font-medium text-sm">
                                {language === 'cr' ? 'Itilizatèr sinyalé:' : 'Utilisateur signalé:'}
                              </p>
                              <p className="text-sm">{report.reported_user.name}</p>
                              <p className="text-xs text-muted-foreground">{report.reported_user.email}</p>
                            </div>
                          )}

                          {/* Description */}
                          {report.description && (
                            <div className="text-sm">
                              <p className="text-xs text-muted-foreground mb-1">
                                {language === 'cr' ? 'Détay:' : 'Détails:'}
                              </p>
                              <p className="text-foreground">{report.description}</p>
                            </div>
                          )}

                          {/* Reporter Info */}
                          <p className="text-xs text-muted-foreground">
                            {language === 'cr' ? 'Sinyalé par:' : 'Signalé par:'} {report.reporter_name || 'Anonyme'}
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex md:flex-col gap-2 p-4 bg-muted/30 md:w-44 justify-center">
                          <Button 
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                              setSelectedReport(report);
                              setReportAction('dismiss');
                            }}
                            data-testid={`dismiss-${report.report_id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            {language === 'cr' ? 'Ignoré' : 'Ignorer'}
                          </Button>
                          {report.post_id && (
                            <Button 
                              variant="destructive"
                              size="sm"
                              className="flex-1"
                              onClick={() => {
                                setSelectedReport(report);
                                setReportAction('delete_post');
                              }}
                              data-testid={`delete-post-${report.report_id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              {language === 'cr' ? 'Siprim' : 'Supprimer'}
                            </Button>
                          )}
                          <Button 
                            variant="outline"
                            size="sm"
                            className="flex-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                            onClick={() => {
                              setSelectedReport(report);
                              setReportAction('warn_user');
                            }}
                            data-testid={`warn-${report.report_id}`}
                          >
                            <MessageSquareWarning className="h-4 w-4 mr-1" />
                            {language === 'cr' ? 'Avèrti' : 'Avertir'}
                          </Button>
                          <Button 
                            variant="outline"
                            size="sm"
                            className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => {
                              setSelectedReport(report);
                              setReportAction('ban_user');
                            }}
                            data-testid={`ban-${report.report_id}`}
                          >
                            <Ban className="h-4 w-4 mr-1" />
                            {language === 'cr' ? 'Bani' : 'Bannir'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Confirmation Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedRequest?.action === 'approve' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-destructive" />
              )}
              {selectedRequest?.action === 'approve' 
                ? (language === 'cr' ? 'Aprouvé la vérifikasyon' : 'Approuver la vérification')
                : (language === 'cr' ? 'Refuzé la vérifikasyon' : 'Refuser la vérification')}
            </DialogTitle>
            <DialogDescription>
              {selectedRequest?.action === 'approve' 
                ? (language === 'cr' 
                    ? `Ou lé sir ou vé aprouvé ${selectedRequest?.business_name} ? Zot va gagn lo baz vérifié.`
                    : `Êtes-vous sûr de vouloir approuver ${selectedRequest?.business_name} ? Ils obtiendront le badge vérifié.`)
                : (language === 'cr'
                    ? `Pourkwa ou refuz ${selectedRequest?.business_name} ?`
                    : `Pourquoi refusez-vous ${selectedRequest?.business_name} ?`)}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Textarea
              placeholder={selectedRequest?.action === 'approve' 
                ? (language === 'cr' ? 'Not opsyonèl...' : 'Note optionnelle...')
                : (language === 'cr' ? 'Rézon lo réfi (obligatwar)' : 'Raison du refus (obligatoire)')}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedRequest(null)}>
              {language === 'cr' ? 'Anilé' : 'Annuler'}
            </Button>
            <Button 
              variant={selectedRequest?.action === 'approve' ? 'default' : 'destructive'}
              onClick={() => handleVerificationAction(selectedRequest?.action)}
              disabled={actionLoading || (selectedRequest?.action === 'reject' && !reviewNotes.trim())}
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedRequest?.action === 'approve' 
                ? (language === 'cr' ? 'Konfirmé' : 'Confirmer')
                : (language === 'cr' ? 'Refuzé' : 'Refuser')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Action Dialog */}
      <Dialog open={!!selectedReport} onOpenChange={() => {
        setSelectedReport(null);
        setReviewNotes('');
        setReportAction('dismiss');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {reportAction === 'dismiss' && <XCircle className="h-5 w-5 text-gray-500" />}
              {reportAction === 'delete_post' && <Trash2 className="h-5 w-5 text-destructive" />}
              {reportAction === 'warn_user' && <MessageSquareWarning className="h-5 w-5 text-orange-500" />}
              {reportAction === 'ban_user' && <Ban className="h-5 w-5 text-red-600" />}
              {reportAction === 'dismiss' && (language === 'cr' ? 'Ignoré sinyalman' : 'Ignorer le signalement')}
              {reportAction === 'delete_post' && (language === 'cr' ? 'Siprim lo post' : 'Supprimer le post')}
              {reportAction === 'warn_user' && (language === 'cr' ? 'Avèrti litilizatèr' : 'Avertir l\'utilisateur')}
              {reportAction === 'ban_user' && (language === 'cr' ? 'Bani litilizatèr' : 'Bannir l\'utilisateur')}
            </DialogTitle>
            <DialogDescription>
              {reportAction === 'dismiss' && (language === 'cr' 
                ? 'Le sinyalman va èt marqué kom réglé san aksyon.'
                : 'Le signalement sera marqué comme traité sans action.')}
              {reportAction === 'delete_post' && (language === 'cr'
                ? 'Le post va èt siprimé définitivman. Lotèr va resévwar in notifikasyon.'
                : 'Le post sera supprimé définitivement. L\'auteur sera notifié.')}
              {reportAction === 'warn_user' && (language === 'cr'
                ? 'In avèrtisman va èt anvoyé a litilizatèr.'
                : 'Un avertissement sera envoyé à l\'utilisateur.')}
              {reportAction === 'ban_user' && (language === 'cr'
                ? 'Litilizatèr va èt bani é pourra pli aksédé son kont.'
                : 'L\'utilisateur sera banni et ne pourra plus accéder à son compte.')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Show reported content summary */}
            {selectedReport?.post && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p className="font-medium">{selectedReport.post.title}</p>
                <p className="text-muted-foreground text-xs mt-1">{selectedReport.post.content}</p>
              </div>
            )}

            <Textarea
              placeholder={
                reportAction === 'dismiss' 
                  ? (language === 'cr' ? 'Not opsyonèl...' : 'Note optionnelle...')
                  : (language === 'cr' ? 'Rézon pou laksyon...' : 'Raison de l\'action...')
              }
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setSelectedReport(null);
              setReviewNotes('');
              setReportAction('dismiss');
            }}>
              {language === 'cr' ? 'Anilé' : 'Annuler'}
            </Button>
            <Button 
              variant={reportAction === 'dismiss' ? 'default' : 'destructive'}
              onClick={handleReportAction}
              disabled={actionLoading}
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {language === 'cr' ? 'Konfirmé' : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
